/**
 * Pipe Parser - Core Extraction Parsing Logic
 *
 * Parses pipe-delimited output from the extraction API and transforms
 * it into structured player data. Ported from mobile/utils/pipe-parser.ts
 * for full feature parity.
 *
 * This module handles two parsing passes:
 * - Pass 1: Structure Analysis (assessment phase) - understands roster format, columns, teams
 * - Pass 2: Raw Player Data - transforms player data with field normalization
 */

import type {
  StructureAnalysis,
  TeamInfo,
  ExtractedPlayerRow,
} from '../types/extraction';
import {
  splitFullName,
  normalizeBats,
  normalizeThrows,
  splitBatsThrows,
  normalizeGradYear,
  normalizeHeight,
  normalizeWeight,
  normalizePhone,
  normalizeSocialHandle,
  normalizeGpa,
} from './field-normalizers';

// =================================================================
// PASS 1 PARSING - STRUCTURE ANALYSIS
// =================================================================

/**
 * Parse Pass 1 structure analysis output
 * Expected format:
 * Line 1: format|roster_type|team_indicator|estimated_player_count
 * Line 2: col1|col2|col3|... (normalized column names)
 * Lines 3+: team_id|team_color|row_start|row_end (if showcase)
 */
export function parseStructureAnalysis(rawOutput: string): StructureAnalysis {
  const lines = rawOutput.trim().split('\n');

  if (lines.length < 2) {
    throw new Error('Invalid structure analysis: Expected at least 2 lines');
  }

  // Parse Line 1: format|roster_type|team_indicator|estimated_player_count
  const [format, roster_type, team_indicator, player_count_str] = lines[0].split('|');

  if (!format || !roster_type || !team_indicator || !player_count_str) {
    throw new Error(`Invalid structure line 1: ${lines[0]}`);
  }

  // Validate format
  if (format !== 'table' && format !== 'card') {
    throw new Error(`Invalid format: ${format}. Expected 'table' or 'card'`);
  }

  // Validate roster_type
  if (roster_type !== 'showcase' && roster_type !== 'single_team') {
    throw new Error(`Invalid roster_type: ${roster_type}. Expected 'showcase' or 'single_team'`);
  }

  // Validate team_indicator
  const valid_indicators = ['explicit_column', 'cell_background_color', 'row_grouping', 'none'];
  if (!valid_indicators.includes(team_indicator)) {
    throw new Error(`Invalid team_indicator: ${team_indicator}`);
  }

  const estimated_player_count = parseInt(player_count_str, 10);
  if (isNaN(estimated_player_count)) {
    throw new Error(`Invalid player count: ${player_count_str}`);
  }

  // Determine column line index - check if line 1 is page_team_name (new format)
  // Old format: line 0 = structure, line 1 = columns
  // New format: line 0 = structure, line 1 = page_team_name, line 2 = columns
  let columnsLineIndex = 1;
  let teamsStartIndex = 2;

  if (lines.length > 1 && lines[1].startsWith('page_team_name|')) {
    columnsLineIndex = 2;
    teamsStartIndex = 3;
  }

  if (lines.length <= columnsLineIndex) {
    throw new Error('Invalid structure analysis: Missing columns line');
  }

  const columns = lines[columnsLineIndex].split('|').map((c) => c.trim());

  // Parse team lines (if showcase)
  let teams: TeamInfo[] | null = null;
  if (roster_type === 'showcase' && lines.length > teamsStartIndex) {
    teams = [];
    for (let i = teamsStartIndex; i < lines.length; i++) {
      const parts = lines[i].split('|');

      if (parts.length >= 4) {
        // 4-field format: team_id|team_color|start|end
        const [team_id, team_color, start_str, end_str] = parts;
        const row_range_start = parseInt(start_str, 10);
        const row_range_end = parseInt(end_str, 10);

        if (isNaN(row_range_start) || isNaN(row_range_end)) {
          console.warn(`[Parser] Invalid row range in team line: ${lines[i]}`);
          continue;
        }

        teams.push({ team_id, team_color, row_range_start, row_range_end });
      } else if (parts.length === 3) {
        // 3-field format: team_id|start|end (no team_color)
        const [team_id, start_str, end_str] = parts;
        const row_range_start = parseInt(start_str, 10);
        const row_range_end = parseInt(end_str, 10);

        if (isNaN(row_range_start) || isNaN(row_range_end)) {
          console.warn(`[Parser] Invalid row range in team line: ${lines[i]}`);
          continue;
        }

        teams.push({ team_id, team_color: team_id, row_range_start, row_range_end });
      } else {
        console.warn(`[Parser] Skipping invalid team line: ${lines[i]}`);
      }
    }
  }

  return {
    format: format as 'table' | 'card',
    roster_type: roster_type as 'showcase' | 'single_team',
    team_indicator: team_indicator as StructureAnalysis['team_indicator'],
    estimated_player_count,
    columns,
    teams,
  };
}

// =================================================================
// PASS 2 PARSING WITH FIELD NORMALIZATION
// =================================================================

/**
 * Raw parsed player data before transformation
 */
interface RawPlayerData {
  [columnName: string]: string;
}

/**
 * Parse Pass 2 raw player data and transform to ExtractedPlayerRow
 * @param rawOutput - Pipe-delimited output from Pass 2
 * @param columns - Column names from Pass 1 (in order)
 * @param structure - Structure analysis from Pass 1
 */
export function parseRawPlayerData(
  rawOutput: string,
  columns: string[],
  structure: StructureAnalysis
): ExtractedPlayerRow[] {
  const lines = rawOutput.trim().split('\n');
  const rows: ExtractedPlayerRow[] = [];

  // Validate column count against first data row
  const firstDataLine = lines.find(l => l.trim().length > 0);
  if (firstDataLine) {
    const firstRowFieldCount = firstDataLine.split('|').length;
    if (firstRowFieldCount !== columns.length) {
      console.error(
        `[COLUMN MISMATCH] Expected ${columns.length} columns but first data row has ${firstRowFieldCount} fields. ` +
        `Columns: ${columns.join(', ')}. First row: ${firstDataLine.substring(0, 200)}`
      );
    }
  }

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    if (!line.trim()) continue;

    let fields = line.split('|');

    // Handle combined bats/throws: Gemini sometimes outputs "R/R" as one field
    // when assessment defines separate bats and throws columns, causing a field shift
    if (fields.length === columns.length - 1) {
      const batsIdx = columns.indexOf('bats');
      const throwsIdx = columns.indexOf('throws');
      if (batsIdx >= 0 && throwsIdx === batsIdx + 1 && batsIdx < fields.length) {
        const batsField = fields[batsIdx].trim();
        if (/^[RLSB][\\/-][RLSB]$/i.test(batsField)) {
          const parts = batsField.split(/[/\-\\]/);
          fields = [
            ...fields.slice(0, batsIdx),
            parts[0],
            parts[1],
            ...fields.slice(batsIdx + 1),
          ];
        }
      }
    }

    // Handle combined name: Gemini sometimes outputs "First Last" as one field
    // when assessment defines separate first_name and last_name columns
    if (fields.length === columns.length - 1) {
      const fnIdx = columns.indexOf('first_name');
      const lnIdx = columns.indexOf('last_name');
      if (fnIdx >= 0 && lnIdx === fnIdx + 1 && fnIdx < fields.length) {
        // first_name|last_name order — split on first space: "Anthony Depompei" → "Anthony", "Depompei"
        const combined = fields[fnIdx].trim();
        const spaceIdx = combined.indexOf(' ');
        if (spaceIdx > 0) {
          fields = [
            ...fields.slice(0, fnIdx),
            combined.substring(0, spaceIdx),
            combined.substring(spaceIdx + 1),
            ...fields.slice(fnIdx + 1),
          ];
        }
      } else if (lnIdx >= 0 && fnIdx === lnIdx + 1 && lnIdx < fields.length) {
        // last_name|first_name order — split on first space: "Depompei Anthony" → "Depompei", "Anthony"
        const combined = fields[lnIdx].trim();
        const spaceIdx = combined.indexOf(' ');
        if (spaceIdx > 0) {
          fields = [
            ...fields.slice(0, lnIdx),
            combined.substring(0, spaceIdx),
            combined.substring(spaceIdx + 1),
            ...fields.slice(lnIdx + 1),
          ];
        }
      }
    }

    if (fields.length !== columns.length) {
      console.warn(
        `[Parser] Row ${lineIndex + 1}: Field count mismatch (expected ${columns.length}, got ${fields.length}): ${line.substring(0, 100)}...`
      );
      // Try to parse anyway if close
      if (Math.abs(fields.length - columns.length) > 2) {
        continue;
      }
    }

    // Map fields to column names
    const rawData: RawPlayerData = {};
    for (let i = 0; i < columns.length && i < fields.length; i++) {
      rawData[columns[i]] = fields[i]?.trim() || '-';
    }

    // Transform to ExtractedPlayerRow
    const player = transformToPlayerRow(rawData, structure, lineIndex);
    if (player) {
      rows.push(player);
    }
  }

  return rows;
}

/**
 * Column names a Class cell arrives under when the model did not use the ones we asked for.
 *
 * The assessment prompt asks for `grad_year` and lists `class` among the headers it covers, but
 * its own fallback rule — "for unrecognized columns: use lowercase with underscores" — means a
 * roster headed `Yr.`, `Cl.`, `Elig.` or `YOG` comes back under that name instead. Every other
 * layer already tolerates those spellings (`autoMapColumns` in FieldMappingStep lists them for
 * the `class` target); this parser did not, and it runs FIRST. A column it does not recognize is
 * not carried under its own name — `buildParsedCSV` emits a fixed field list — so the cell was
 * dropped before the field-mapping screen could offer it, and a JUCO roster reached the JC-year
 * review with "No class" on every row and no way to tell where the data went.
 *
 * Keys are normalized the way `normalizeColumnName` normalizes them, so one entry covers `yr`,
 * `Yr.` and `YR`. Deliberately absent: anything that is not a class column on some roster
 * somewhere — `age`, `dob`, `season`.
 */
const CLASS_COLUMN_ALIASES: ReadonlySet<string> = new Set(
  [
    'cl', 'yr', 'year', 'grade', 'grad', 'gradyr', 'gradyear', 'graduation',
    'graduationyear', 'classyear', 'academicyear', 'eligibility', 'elig', 'yog',
  ],
);

/** Lowercase, separators stripped: `Grad Yr.` and `grad_yr` collapse onto one key. */
function normalizeColumnName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * The Class cell hiding under a column name we did not ask for, or `''` when there is none.
 *
 * Only consulted after `class` and `grad_year` have had their say, so a roster that used the
 * names the prompt asked for reads exactly as it always did. Columns are searched in roster
 * order, and the `-` a missing value arrives as does not count as an answer.
 */
function readAliasedClassCell(raw: RawPlayerData): string {
  for (const [column, value] of Object.entries(raw)) {
    if (!value || value === '-') continue;
    if (CLASS_COLUMN_ALIASES.has(normalizeColumnName(column))) return value;
  }
  return '';
}

/**
 * Column names a School cell arrives under when the model did not use the one we asked for.
 *
 * The same failure as CLASS_COLUMN_ALIASES: the prompt asks for `school`, but a roster headed
 * "School Name" or "University Name" often comes back as `school_name` / `university_name`,
 * and `buildParsedCSV` drops any column it does not know. The API folds these into `school`
 * too (RosterSchoolColumn); this covers extractions stored before it did, and the Pass 1
 * fallback, which reads the raw assessment. Keep the two lists in step.
 */
const SCHOOL_COLUMN_ALIASES: ReadonlySet<string> = new Set(
  [
    'schoolname', 'highschool', 'highschoolname', 'hs', 'hsname',
    'university', 'universityname', 'college', 'collegename',
    'currentschool', 'currentschoolname',
  ],
);

/** The School cell under an aliased column name, or `''` when there is none. */
function readAliasedSchoolCell(raw: RawPlayerData): string {
  for (const [column, value] of Object.entries(raw)) {
    if (!value || value === '-') continue;
    if (SCHOOL_COLUMN_ALIASES.has(normalizeColumnName(column))) return value;
  }
  return '';
}

/**
 * Transform raw player data to ExtractedPlayerRow with normalization
 */
function transformToPlayerRow(
  raw: RawPlayerData,
  structure: StructureAnalysis,
  rowIndex: number
): ExtractedPlayerRow | null {
  // Handle bats/throws - could be combined or separate
  let bats = '-';
  let throws_val = '-';

  if (raw.bats_throws) {
    const split = splitBatsThrows(raw.bats_throws);
    bats = split.bats;
    throws_val = split.throws;
  } else {
    // Check if bats field itself contains a combined value (e.g., "R/R") when throws is empty
    if (raw.bats && !raw.throws && /[/\-\\\s]/.test(raw.bats)) {
      const split = splitBatsThrows(raw.bats);
      bats = split.bats;
      throws_val = split.throws;
    } else {
      if (raw.bats) bats = normalizeBats(raw.bats);
      if (raw.throws) throws_val = normalizeThrows(raw.throws);
    }
  }

  // Determine team assignment
  let team_id = raw.team_id || '-';
  let team_color = raw.team_color || '-';

  // For non-explicit team indicators, assign based on row position
  if (structure.roster_type === 'showcase' && structure.teams) {
    if (
      structure.team_indicator === 'cell_background_color' ||
      structure.team_indicator === 'row_grouping'
    ) {
      // Find which team this row belongs to based on row ranges
      const rowNum = rowIndex + 1; // 1-indexed for matching
      for (const team of structure.teams) {
        if (rowNum >= team.row_range_start && rowNum <= team.row_range_end) {
          team_id = team.team_id;
          team_color = team.team_color;
          break;
        }
      }
    }

    // Look up team_color from structure.teams if not in row data
    if (team_id !== '-' && team_color === '-') {
      const matchedTeam = structure.teams.find((t) => t.team_id === team_id);
      if (matchedTeam) {
        team_color = matchedTeam.team_color;
      }
    }
  }

  // Determine name fields - handle both single 'name' and split 'first_name'/'last_name'
  let name_first = '-';
  let name_last = '-';

  if ((raw.name || raw.full_name) && !raw.first_name && !raw.last_name) {
    // Single 'name' or 'full_name' field - split it
    const nameParts = splitFullName(raw.name || raw.full_name);
    name_first = nameParts.firstName;
    name_last = nameParts.lastName;
  } else {
    // Separate fields exist
    name_first = raw.first_name || '-';
    name_last = raw.last_name || '-';
  }

  // The Class cell as a last resort, when the model named its column something we did not ask
  // for. See CLASS_COLUMN_ALIASES for why this cannot be left to the field-mapping screen.
  const aliasedClassCell = readAliasedClassCell(raw);

  // Build player row with normalized values
  const player: ExtractedPlayerRow = {
    roster_type: structure.roster_type,
    team_id,
    team_color,
    number: raw.number || '-',
    name_last,
    name_first,
    position: raw.position || '-',
    bats,
    throws: throws_val,
    grad_year: normalizeGradYear(raw.grad_year || raw.class || aliasedClassCell || '-'),
    // The raw Class cell, kept beside the normalized grad_year. Reading a class off grad_year is
    // impossible once it has been turned into a year, and the review step needs the class.
    class: raw.class || raw.grad_year || aliasedClassCell || '-',
    school: raw.school || readAliasedSchoolCell(raw) || '-',
    email: raw.email || '-',
    notes: raw.notes || raw.note || raw.comments || raw.comment || raw.remarks || '-',
    // Build stats_csv from individual fields
    stats_csv: buildStatsCsv(raw),
    // Build contact_csv from individual fields
    contact_csv: buildContactCsv(raw),
  };

  // Validate we have at least some identifying info
  if (player.name_last === '-' && player.name_first === '-' && player.number === '-') {
    console.warn(`[Parser] Row ${rowIndex + 1}: Skipping row with no identifying info`);
    return null;
  }

  return player;
}

/**
 * Build stats_csv from individual stat fields
 * Format: ht:6-1;wt:180;gpa:3.8;sat:1200;act:28
 */
function buildStatsCsv(raw: RawPlayerData): string {
  const parts: string[] = [];

  if (raw.height && raw.height !== '-') {
    parts.push(`ht:${normalizeHeight(raw.height)}`);
  }
  if (raw.weight && raw.weight !== '-') {
    parts.push(`wt:${normalizeWeight(raw.weight)}`);
  }
  if (raw.gpa && raw.gpa !== '-') {
    const gpa = normalizeGpa(raw.gpa);
    if (gpa !== '-') parts.push(`gpa:${gpa}`);
  }
  if (raw.sat && raw.sat !== '-') {
    parts.push(`sat:${raw.sat}`);
  }
  if (raw.act && raw.act !== '-') {
    parts.push(`act:${raw.act}`);
  }

  return parts.length > 0 ? parts.join(';') : '-';
}

/**
 * Build contact_csv from individual contact fields
 * Format: phone:555-1234;twitter:handle;instagram:user;city:Denver;state:CO;address:123 Main St
 */
function buildContactCsv(raw: RawPlayerData): string {
  const parts: string[] = [];

  if (raw.phone && raw.phone !== '-') {
    parts.push(`phone:${normalizePhone(raw.phone)}`);
  }
  if (raw.twitter && raw.twitter !== '-') {
    parts.push(`twitter:${normalizeSocialHandle(raw.twitter)}`);
  }
  if (raw.instagram && raw.instagram !== '-') {
    parts.push(`instagram:${normalizeSocialHandle(raw.instagram)}`);
  }
  // Locality, from whichever column names the extraction used. `hometown` and `st` are the
  // mapping prompt's standard target names for City and State; `city`/`state` only turn up on the
  // image-extraction path. A combined cell ("Palm Beach Gardens, FL") can arrive under either
  // `hometown_st` or `hometown`. A combined cell wins over the separate columns, which is the
  // precedence this function has always had.
  let city: string | undefined;
  let state: string | undefined;

  const combined = cell(raw.hometown_st) ?? cell(raw.hometown);
  if (combined) {
    const commaIdx = combined.lastIndexOf(',');
    if (commaIdx > 0) {
      city = combined.substring(0, commaIdx).trim();
      state = combined.substring(commaIdx + 1).trim();
    } else {
      city = combined;
    }
  }
  city ??= cell(raw.city);
  state ??= cell(raw.state) ?? cell(raw.st);

  // A Zipcode column is not one of the prompt's standard target names, so it arrives under its
  // own snake_case name.
  const zip = cell(raw.zip) ?? cell(raw.zipcode) ?? cell(raw.postal_code);

  const street = cell(raw.address);
  if (street) {
    parts.push(`address:${street}`);
  }
  if (city) {
    parts.push(`city:${city}`);
  }
  if (state) {
    parts.push(`state:${state}`);
  }
  if (zip) {
    parts.push(`zip:${zip}`);
  }

  return parts.length > 0 ? parts.join(';') : '-';
}

/** A trimmed cell value, or undefined when the cell is empty or the extraction's "-" placeholder. */
function cell(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed !== '-' ? trimmed : undefined;
}

